import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { PropsWithChildren } from 'react';
import styled from 'styled-components';

import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop.ts';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery.ts';

import { BoardWebAppHeader } from '#src/components/navigation/NewAppHeader/WebAppHeader/BoardWebAppHeader/index.tsx';
import { SeriesBoardTrckSwitcher } from '#src/components/series/SeriesBoard/SeriesBoardTrckSwitcher/index.tsx';

import type { SeriesBoardHeader_squad } from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  padding: var(--unit) var(--double-unit) 0 var(--double-unit);

  @media ${desktopAndAbove} {
    padding: var(--unit) 0 0 var(--double-unit);
  }
`;

type Props = {
  squad: Nullable<SeriesBoardHeader_squad>;
  selectedTrack: 'individual' | 'squad';
  isReplay?: boolean;
  hideTrackSwitcher?: boolean;
};

export const SeriesBoardHeader = ({
  squad,
  selectedTrack,
  children,
  isReplay,
  hideTrackSwitcher,
}: PropsWithChildren<Props>) => {
  const seriesNav = useSeriesNav();

  const isLaptopAndAbove = useIsLaptop();
  const showUserNav = seriesNav && !isLaptopAndAbove;

  return (
    <Root>
      {!!showUserNav && <BoardWebAppHeader>{children}</BoardWebAppHeader>}
      <SBHorizontal>
        {!hideTrackSwitcher && (
          <SeriesBoardTrckSwitcher
            squad={squad}
            selected={selectedTrack}
            isReplay={isReplay}
          />
        )}
        {!showUserNav && children}
      </SBHorizontal>
    </Root>
  );
};

SeriesBoardHeader.fragments = {
  squad: gql`
    fragment SeriesBoardHeader_squad on Squad {
      id
      ...SeriesBoardTrckSwitcher_squad
    }
    ${SeriesBoardTrckSwitcher.fragments.squad}
  ` as TypedDocumentNode<SeriesBoardHeader_squad>,
};
